/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { projectCreationContentLeftMargin } from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { QnaMakerProjectCreationSteps } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/QnaMakerProjectCreationModal';

export type ProjectCreationModalFooterProps = {
    activeStep: QnaMakerProjectCreationSteps;
    onNext: () => void;
    onDismiss: () => void;
    onPrevious?: () => void;
    isValid?: boolean;
    isFooterDisabled?: boolean;
    isLoading?: boolean;
    isBackDisabled?: boolean;
};

const messages = defineMessages({
    back: {
        id: 'qnaMaker.qnaMakerProjectModalFooter.back',
        defaultMessage: 'Back'
    },
    next: {
        id: 'qnaMaker.qnaMakerProjectModalFooter.next',
        defaultMessage: 'Next'
    },
    createProject: {
        id: 'qnaMaker.qnaMakerProjectModalFooter.createProject',
        defaultMessage: 'Create project'
    }
});

const { styleComponent } = getStylistV2('ProjectCreationModalFooter');
const modalTheme = getScopedTheme('ProjectCreationModalBase');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const Wrapper = styleComponent<IStackProps>(Stack)('Wrapper', () => ({ borderTop: borderStyle, width: '100%' }));

export const QnaMakerProjectModalFooter = (props: ProjectCreationModalFooterProps) => {
    const {
        activeStep,
        onPrevious,
        onNext,
        onDismiss,
        isLoading = false,
        isBackDisabled = false,
        isFooterDisabled = false,
        isValid = true
    } = props;
    const { localize } = useLocalization();
    const { isMediumOrGreater } = useResponsiveMode();
    const isCreateProjectButtonDisabled = activeStep !== QnaMakerProjectCreationSteps.ReviewAndFinish;

    return (
        <Wrapper
            horizontal={isMediumOrGreater}
            horizontalAlign="space-between"
            verticalAlign="end"
            tokens={{ padding: `16px 5px 16px ${isMediumOrGreater ? projectCreationContentLeftMargin : '5px'}`, childrenGap: 6 }}>
            <Stack
                horizontal={isMediumOrGreater}
                tokens={{
                    childrenGap: 8
                }}>
                <DefaultButton disabled={isBackDisabled || !Boolean(onPrevious) || isFooterDisabled} onClick={onPrevious}>
                    {localize(messages.back)}
                </DefaultButton>
                <PrimaryButton
                    disabled={isFooterDisabled || !isValid || !isCreateProjectButtonDisabled}
                    onClick={onNext}
                    data-automation-id="nextButton">
                    {localize(messages.next)}
                </PrimaryButton>
                {isLoading && <Spinner />}
            </Stack>

            <Stack
                horizontal={isMediumOrGreater}
                tokens={{
                    childrenGap: 8
                }}>
                <PrimaryButton
                    disabled={isFooterDisabled || !isValid || isCreateProjectButtonDisabled}
                    data-automation-id="createProjectButton"
                    onClick={onNext}>
                    {localize(messages.createProject)}
                </PrimaryButton>
                <DefaultButton disabled={isFooterDisabled} onClick={onDismiss}>
                    {localize(globalMessages.cancel)}
                </DefaultButton>
            </Stack>
        </Wrapper>
    );
};
